import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, Input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { collectKeys, type TranslationKey } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { ActivityType } from "@ankaadia/graphql";
import { noop } from "lodash";
import { MultiSelect } from "primeng/multiselect";
import { EnumPipe } from "../../../shared/pipes/enum.pipe";
import { NewUiMultiSelectSortOnTopDirective } from "../../../shared/primeng/multi-select-sort-selected-values-on-top/new-ui-multi-select-sort-on-top.directive";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { TalentPoolConfigService } from "../../talent-pool-config/talent-pool-config.service";
import { NewUiActivityIconComponent } from "../activity-icon/new-ui-activity-icon.component";

@Component({
  selector: "new-ui-activity-type-selector",
  templateUrl: "./new-ui-activity-type-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: NewUiActivityTypeSelectorComponent, multi: true }],
  imports: [
    TranslateDirective,
    MultiSelect,
    NewUiMultiSelectSortOnTopDirective,
    FormsModule,
    NewUiActivityIconComponent,
    EnumPipe,
  ],
})
export class NewUiActivityTypeSelectorComponent implements ControlValueAccessor {
  private readonly talentPoolConfigService = inject(TalentPoolConfigService);
  private readonly showTalentPoolUi = toSignal(this.talentPoolConfigService.showTalentPoolUi(), {
    initialValue: false,
  });

  //Record to force order + completeness
  private static readonly activityTypeLabels: Record<ActivityType, TranslationKey> = {
    CandidateAdded: "enum.ActivityType.CandidateAdded",
    CandidateDeleted: "enum.ActivityType.CandidateDeleted",
    CandidateUpdated: "enum.ActivityType.CandidateUpdated",
    CandidateBirthday: "enum.ActivityType.CandidateBirthday",
    CandidateBirthdaySoon: "enum.ActivityType.CandidateBirthdaySoon",
    CandidateDeletionReminder: "enum.ActivityType.CandidateDeletionReminder",
    CandidateRequestDeletion: "enum.ActivityType.CandidateRequestDeletion",
    CandidateOptedOut: "enum.ActivityType.CandidateOptedOut",
    CandidateOptedIn: "enum.ActivityType.CandidateOptedIn",
    CandidateDroppedOut: "enum.ActivityType.CandidateDroppedOut",
    CandidateLoggedIn: "enum.ActivityType.CandidateLoggedIn",
    CandidateNotFound: "enum.ActivityType.CandidateNotFound",
    CandidateAddedToCollection: "enum.ActivityType.CandidateAddedToCollection",
    CandidateRemovedFromCollection: "enum.ActivityType.CandidateRemovedFromCollection",
    CandidateAddedToExam: "enum.ActivityType.CandidateAddedToExam",
    CandidateRemovedFromExam: "enum.ActivityType.CandidateRemovedFromExam",
    CandidateTransferred: "enum.ActivityType.CandidateTransferred",

    FileUploaded: "enum.ActivityType.FileUploaded",
    FileModified: "enum.ActivityType.FileModified",
    FileDeleted: "enum.ActivityType.FileDeleted",
    FileExpiresSoon: "enum.ActivityType.FileExpiresSoon",
    DocumentSetMetaDataModified: "enum.ActivityType.DocumentSetMetaDataModified",
    FreeFormatFileUploaded: "enum.ActivityType.FreeFormatFileUploaded",
    FreeFormatFileDeleted: "enum.ActivityType.FreeFormatFileDeleted",
    TranslationUploaded: "enum.ActivityType.TranslationUploaded",
    TranslationFailed: "enum.ActivityType.TranslationFailed",
    CvParsingRequested: "enum.ActivityType.CvParsingRequested",
    DocumentNotFound: "enum.ActivityType.DocumentNotFound",

    CollectionAdded: "enum.ActivityType.CollectionAdded",
    CollectionDeleted: "enum.ActivityType.CollectionDeleted",
    CollectionUpdated: "enum.ActivityType.CollectionUpdated",
    CollectionEmbeddedIntoCollection: "enum.ActivityType.CollectionEmbeddedIntoCollection",
    CollectionRemovedFromCollection: "enum.ActivityType.CollectionRemovedFromCollection",
    CollectionNotFound: "enum.ActivityType.CollectionNotFound",

    SharingAdded: "enum.ActivityType.SharingAdded",
    SharingDeleted: "enum.ActivityType.SharingDeleted",
    SharingUpdated: "enum.ActivityType.SharingUpdated",
    SharingEnabled: "enum.ActivityType.SharingEnabled",
    SharingDisabled: "enum.ActivityType.SharingDisabled",
    SharingTypeModified: "enum.ActivityType.SharingTypeModified",
    InvalidSharingTemplate: "enum.ActivityType.InvalidSharingTemplate",
    ProfileMaskUpdated: "enum.ActivityType.ProfileMaskUpdated",

    ProcessCreated: "enum.ActivityType.ProcessCreated",
    ProcessDeleted: "enum.ActivityType.ProcessDeleted",
    ProcessReset: "enum.ActivityType.ProcessReset",
    ProcessNotFound: "enum.ActivityType.ProcessNotFound",
    ProcessModelPublished: "enum.ActivityType.ProcessModelPublished",
    ProcessModelUnpublished: "enum.ActivityType.ProcessModelUnpublished",
    ProcessModelReleaseCreated: "enum.ActivityType.ProcessModelReleaseCreated",
    ProcessModelDeleted: "enum.ActivityType.ProcessModelDeleted",
    ProcessModelNotFound: "enum.ActivityType.ProcessModelNotFound",
    BaseProcessNotFound: "enum.ActivityType.BaseProcessNotFound",

    TaskManuallyCreated: "enum.ActivityType.TaskManuallyCreated",
    TaskDeleted: "enum.ActivityType.TaskDeleted",
    TaskReset: "enum.ActivityType.TaskReset",
    TaskStarted: "enum.ActivityType.TaskStarted",
    TaskFinished: "enum.ActivityType.TaskFinished",
    TaskCancelled: "enum.ActivityType.TaskCancelled",
    TaskStopped: "enum.ActivityType.TaskStopped",
    TaskFailed: "enum.ActivityType.TaskFailed",
    TaskChanged: "enum.ActivityType.TaskChanged",
    TaskManuallyChanged: "enum.ActivityType.TaskManuallyChanged",
    TaskReminderSent: "enum.ActivityType.TaskReminderSent",
    TaskDelegatedByRule: "enum.ActivityType.TaskDelegatedByRule",
    TaskDelegationRevokedByRule: "enum.ActivityType.TaskDelegationRevokedByRule",
    TaskStateChanged: "enum.ActivityType.TaskStateChanged",
    TaskStateManuallyChanged: "enum.ActivityType.TaskStateManuallyChanged",
    TaskNoteAdded: "enum.ActivityType.TaskNoteAdded",
    TaskNotFound: "enum.ActivityType.TaskNotFound",

    EmailNotDelivered: "enum.ActivityType.EmailNotDelivered",
    OnlineApplicationRun: "enum.ActivityType.OnlineApplicationRun",
    ConsentFeedback: "enum.ActivityType.ConsentFeedback",

    FeedbackProvided: "enum.ActivityType.FeedbackProvided",
    FeedbackCommentAdded: "enum.ActivityType.FeedbackCommentAdded",

    AutoProcessOperationTriggered: "enum.ActivityType.AutoProcessOperationTriggered",
    AutoProcessOperationFailed: "enum.ActivityType.AutoProcessOperationFailed",
    AutoTeamRoleNotSetOrOwner: "enum.ActivityType.AutoTeamRoleNotSetOrOwner",
    AutoTeamNoResponsibleUser: "enum.ActivityType.AutoTeamNoResponsibleUser",
    InvalidUserGroupRoleMapping: "enum.ActivityType.InvalidUserGroupRoleMapping",
    OrganizationNotLinked: "enum.ActivityType.OrganizationNotLinked",
    OrganizationNotFound: "enum.ActivityType.OrganizationNotFound",
    OrganizationLinkInvitationAccepted: "enum.ActivityType.OrganizationLinkInvitationAccepted",
    ChildOrganizationLinksSynced: "enum.ActivityType.ChildOrganizationLinksSynced",

    UserLoggedIn: "enum.ActivityType.UserLoggedIn",
    UserNotFound: "enum.ActivityType.UserNotFound",

    EducationExamCreated: "enum.ActivityType.EducationExamCreated",
    EducationExamUpdated: "enum.ActivityType.EducationExamUpdated",
    EducationExamDeleted: "enum.ActivityType.EducationExamDeleted",
    EducationExamResultCreated: "enum.ActivityType.EducationExamResultCreated",
    EducationExamResultDeleted: "enum.ActivityType.EducationExamResultDeleted",

    ContractCreated: "enum.ActivityType.ContractCreated",
    ContractAnnulled: "enum.ActivityType.ContractAnnulled",
    ContractCancelled: "enum.ActivityType.ContractCancelled",
    ContractCancellationWithdrawn: "enum.ActivityType.ContractCancellationWithdrawn",
    ContractPlanChanged: "enum.ActivityType.ContractPlanChanged",
    InvoicePaid: "enum.ActivityType.InvoicePaid",

    InterviewIterationAdded: "enum.ActivityType.InterviewIterationAdded",
    InterviewIterationDeleted: "enum.ActivityType.InterviewIterationDeleted",
    InterviewEmployerUpdated: "enum.ActivityType.InterviewEmployerUpdated",
    InterviewFeedbackAdded: "enum.ActivityType.InterviewFeedbackAdded",
    InterviewFeedbackUpdated: "enum.ActivityType.InterviewFeedbackUpdated",
    InterviewFeedbackDeleted: "enum.ActivityType.InterviewFeedbackDeleted",
    InterviewCommentAdded: "enum.ActivityType.InterviewCommentAdded",
    InterviewCommentUpdated: "enum.ActivityType.InterviewCommentUpdated",
    InterviewCommentDeleted: "enum.ActivityType.InterviewCommentDeleted",

    TalentPoolRequestReceived: "enum.ActivityType.TalentPoolRequestReceived",
    TalentPoolRequestApproved: "enum.ActivityType.TalentPoolRequestApproved",
    TalentPoolRequestRejected: "enum.ActivityType.TalentPoolRequestRejected",
  };

  private static readonly talentPoolActivityTypes: readonly ActivityType[] = [
    ActivityType.TalentPoolRequestReceived,
    ActivityType.TalentPoolRequestApproved,
    ActivityType.TalentPoolRequestRejected,
  ];

  private static readonly activityFilterBlackList: readonly ActivityType[] = [
    ActivityType.ProfileMaskUpdated,
    ActivityType.CvParsingRequested,
    ActivityType.TranslationFailed,
  ];

  readonly activityTypes = computed(() =>
    collectKeys(NewUiActivityTypeSelectorComponent.activityTypeLabels)
      .filter((type) => !NewUiActivityTypeSelectorComponent.activityFilterBlackList.includes(type))
      .filter(
        (type) => this.showTalentPoolUi() || !NewUiActivityTypeSelectorComponent.talentPoolActivityTypes.includes(type)
      )
      .map((type) => ({
        label: translateSafely(NewUiActivityTypeSelectorComponent.activityTypeLabels[type]),
        value: type,
      }))
  );

  @Input()
  inputId: string;

  @Input()
  formControl: FormControl;

  model: string[];
  disabled: boolean;
  onModelChange = noop;
  onModelTouched = noop;

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  writeValue(model: string[]): void {
    this.model = model;
    this.changeDetector.markForCheck();
  }

  registerOnChange(onModelChange: () => void): void {
    this.onModelChange = onModelChange;
  }

  registerOnTouched(onModelTouched: () => void): void {
    this.onModelTouched = onModelTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetector.markForCheck();
  }

  updateModel(model: string[]): void {
    this.model = model;
    this.onModelChange(model);
    this.formControl?.setValue(model);
  }
}
